import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuizApi } from "../../components/SobQuiz/hooks/useQuizApi";
import { useQuizConsent } from "../../components/SobQuiz/hooks/useQuizConsent";
import { useQuizProgress } from "../../components/SobQuiz/hooks/useQuizProgress";
import { useQuizState } from "../../components/SobQuiz/hooks/useQuizState";
import { useQuizTracking } from "../../components/SobQuiz/hooks/useQuizTracking";
import { useUser } from "../../hooks/useUser";
export const useQuiz = (quizData) => {
    const { user, isUserLoggedIn } = useUser();
    const [isLoading, setIsLoading] = useState(true);
    const userInfo = useMemo(() => ({
        uuid: user?.uuid,
        email: user?.email,
        isLoggedIn: isUserLoggedIn,
    }), [user?.uuid, user?.email, isUserLoggedIn]);
    const { state, initializeState, handleAnswer, completeQuiz } = useQuizState();
    const { currentQuestion, currentStep, progressToNextQuestion, initializeProgress } = useQuizProgress(quizData);
    const { checkQuizState, submitQuizAnswer } = useQuizApi(quizData, userInfo.uuid, userInfo.email);
    const { consentData, submitConsent, checkConsent } = useQuizConsent(quizData, userInfo.uuid);
    const { trackQuizProgress, trackQuizCompletion } = useQuizTracking();
    const initializeQuiz = useCallback(async () => {
        if (!userInfo.isLoggedIn || !userInfo.uuid) {
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const hasConsent = await checkConsent();
            await initializeState(hasConsent);
            if (hasConsent) {
                const previousState = await checkQuizState();
                if (previousState?.quiz_payload.results[0]) {
                    const lastAnswer = previousState.quiz_payload.results[0].answers[previousState.quiz_payload.results[0].answers.length - 1];
                    initializeProgress(lastAnswer.answer_id, previousState.quiz_payload.results[0].answers.length);
                }
                else {
                    initializeProgress();
                }
            }
        }
        catch (e) {
            console.error("Can't initialize a quiz", e);
        }
        finally {
            setIsLoading(false);
        }
    }, [userInfo]);
    const createAnswer = useCallback((answerId) => {
        if (!currentQuestion)
            return null;
        return {
            answer_id: answerId,
            question_id: currentQuestion.question.id,
            type: "SOURCE_OF_BUSINESS",
            reference_name: currentQuestion.possibleAnswers.find((a) => a.id === answerId)?.referenceName || "",
            local_translation: currentQuestion.possibleAnswers.find((a) => a.id === answerId)?.localTranslation || "",
        };
    }, [currentQuestion]);
    const handleConsentSubmit = useCallback(async (hasConsent) => {
        if (!userInfo.isLoggedIn || !userInfo.uuid)
            return;
        setIsLoading(true);
        try {
            const success = await submitConsent(hasConsent);
            if (!success)
                return;
            await trackQuizProgress(1, undefined, hasConsent.toString(), consentData?.name);
            if (hasConsent) {
                await initializeState(true);
                initializeProgress("", 2);
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [userInfo, consentData]);
    const handleAnswerSubmit = useCallback(async (answerId) => {
        if (!userInfo.isLoggedIn || !userInfo.uuid)
            return;
        const answer = createAnswer(answerId);
        if (!answer)
            return;
        setIsLoading(true);
        try {
            const stateResult = await handleAnswer(answer);
            if (!stateResult)
                return;
            await submitQuizAnswer(stateResult.context.answers);
            const hasNextQuestion = progressToNextQuestion(answerId);
            if (!hasNextQuestion) {
                await completeQuiz();
                await trackQuizCompletion();
            }
            else {
                await trackQuizProgress(currentStep, currentQuestion, answerId);
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [
        userInfo.isLoggedIn,
        userInfo.uuid,
        handleAnswer,
        submitQuizAnswer,
        progressToNextQuestion,
        currentStep,
        currentQuestion,
        completeQuiz,
        trackQuizProgress,
        trackQuizCompletion,
    ]);
    useEffect(() => {
        initializeQuiz();
    }, [initializeQuiz]);
    return {
        state,
        currentQuestion,
        isLoading,
        consentData,
        handleConsent: handleConsentSubmit,
        handleAnswer: handleAnswerSubmit,
    };
};
//# sourceMappingURL=useQuiz.js.map